import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import OptimizedImage from "./OptimizedImage";

interface Testimonial {
  id: string;
  client_name: string;
  service_type: string;
  testimonial_text: string;
  before_image_url: string | null;
  after_image_url: string | null;
  is_case_study: boolean;
  treatment_details: string | null;
}

const Testimonials = () => {
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        console.log('Fetching testimonials...');
        const { data, error } = await supabase
          .from('testimonials')
          .select('*')
          .eq('featured', true)
          .limit(3);

        if (error) {
          console.error('Error fetching testimonials:', error);
          return;
        }

        console.log('Fetched testimonials:', data);
        setTestimonials(data || []);
      } catch (error) {
        console.error('Unexpected error fetching testimonials:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTestimonials();
  }, []);

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {[1, 2, 3].map((i) => (
          <div key={i} className="animate-pulse">
            <div className="bg-gray-200 h-64 rounded-lg mb-4" />
            <div className="h-4 bg-gray-200 rounded w-3/4 mb-2" />
            <div className="h-4 bg-gray-200 rounded w-1/2" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
      {testimonials.map((testimonial) => (
        <div key={testimonial.id} className="bg-white rounded-lg shadow-lg overflow-hidden">
          {testimonial.is_case_study && testimonial.before_image_url && testimonial.after_image_url && (
            <div className="relative">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <OptimizedImage
                    src={testimonial.before_image_url}
                    alt="Before treatment"
                    className="w-full h-48 object-cover"
                  />
                  <span className="absolute top-2 left-2 bg-black/50 text-white px-2 py-1 text-sm rounded">
                    Before
                  </span>
                </div>
                <div>
                  <OptimizedImage
                    src={testimonial.after_image_url}
                    alt="After treatment"
                    className="w-full h-48 object-cover"
                  />
                  <span className="absolute top-2 right-2 bg-black/50 text-white px-2 py-1 text-sm rounded">
                    After
                  </span>
                </div>
              </div>
            </div>
          )}
          <div className="p-6">
            <p className="text-gray-600 mb-4">{testimonial.testimonial_text}</p>
            <div className="flex justify-between items-center">
              <div>
                <p className="font-semibold">{testimonial.client_name}</p>
                <p className="text-sm text-gray-500">{testimonial.service_type}</p>
              </div>
              {testimonial.is_case_study && (
                <span className="bg-primary/10 text-primary px-3 py-1 rounded-full text-sm">
                  Case Study
                </span>
              )}
            </div>
            {testimonial.treatment_details && (
              <p className="mt-4 text-sm text-gray-600">{testimonial.treatment_details}</p>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default Testimonials;